import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RemoveIcon from '@mui/icons-material/Remove';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    IconButton,
    Typography
} from '@mui/material';
import { UtmStatusBadge } from 'common/components/sharedCustomComponents/StatusBadge/UtmStatusBadge';
import { ErrorBoundary } from 'common/hocs/ErrorBoundary';
import { dateToFrontEndFormat } from 'common/utils/dateHelpers';
import { UserProfileAclsList } from 'features/userProfileAcls/UserProfileAcls';
import { UserProfileClaimsList } from 'features/userProfileClaims/UserProfileClaims';
import { UserProfileGlobalList } from 'features/userProfileGlobalRoles/UserProfileGlobalRolesList';
import { UserProfileLicensesList } from 'features/userProfileLicenses/UserProfileLicenses';
import { UserProfileRolesList } from 'features/userProfileRoles/UserProfileRolesList';
import { UserProfileUserGroups } from 'features/userProfileUserGroups/UserProfileUserGroups';

import {
    UserProfileTenantDisplayName,
    UserProfileTenantMembershipDates
} from './components';
import { IUserAppSuiteAndTenants, IUserProfileTenant } from './UserProfileApps';

interface IUserProfileAppsTenants {
    appSuite: IUserAppSuiteAndTenants;
    handleOpenActionsMenu: (row: {
        appSuiteName: string;
        tenant: IUserProfileTenant;
        startDate: string;
        endDate?: string;
    }) => React.MouseEventHandler<HTMLButtonElement>;
    handleSetEditingRow: (row: {
        appSuiteName: string;
        tenant: IUserProfileTenant;
        startDate: string;
        endDate?: string;
    }) => () => void;
}

export const UserProfileAppsTenants = (props: IUserProfileAppsTenants)=> {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const { appSuiteName, tenants } = props.appSuite;
    const startDate = '';

    const handleChange = (tenant: IUserProfileTenant) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(exp => ({ ...exp, [tenant.id]: isExpanded }));
    };

    return (
        <div className="accordions-section" data-testid='accordions-section-test-id'>
            <Typography sx={{
                display: 'flex',
                margin: '0.5rem 0'
            }}>{appSuiteName}</Typography>
            {tenants.map((tenant)=> {
                return (
                    <Accordion
                        expanded={expanded[tenant.id] || false}
                        onChange={handleChange(tenant)}
                        key={tenant.id}
                        sx={{
                            marginBottom: '1rem',
                            boxShadow: 'unset',
                            borderRadius: '5px',
                            border: 1,
                            borderColor: 'info.dark',
                            backgroundColor: 'secondary.light',
                            position: 'unset',
                            '& .MuiAccordion-root': { position: 'unset' },
                            '& .MuiAccordionSummary-expandIconWrapper': { order: -1, pointerEvents: 'auto' },
                            '& .MuiAccordionSummary-content': { display: 'contents' },
                        }}
                        slotProps={{ transition: { unmountOnExit: true } }}
                    >
                        <AccordionSummary
                            data-testid={`${tenant.name}-accordion-summary-test-id`}
                            expandIcon={expanded[tenant.id] ? <RemoveIcon id='expanded'/> : <AddIcon/>}
                            aria-controls="panel1bh-content"
                            id={tenant.id}
                            sx={{
                                height: '4rem',
                                paddingRight: '5px'
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                flexGrow: 1,
                            }}>
                                <UserProfileTenantDisplayName tenantName={tenant.name}/>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                flexGrow: 1
                            }}>
                                <UserProfileTenantMembershipDates
                                    wrapperTestId={tenant.name}
                                    startDate={dateToFrontEndFormat(tenant?.startDate)}
                                    endDate={dateToFrontEndFormat(tenant?.endDate)}
                                />
                                <UtmStatusBadge
                                    tooltipProps={tenant}
                                    statusTestId={tenant.name}
                                    status={tenant.status}
                                    chipSize='medium'
                                />
                                <IconButton
                                    data-testid={`${tenant.name}-menu-button`}
                                    size='small'
                                    edge="end"
                                    onClick={props.handleOpenActionsMenu({ appSuiteName, tenant, startDate })}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </div>
                        </AccordionSummary>
                        <ErrorBoundary>
                            <AccordionDetails
                                data-testid={`${tenant.name}-accordion-details-test-id`}
                            >
                                <section
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    <UserProfileGlobalList
                                        tenant={tenant}
                                        handleSetEditingRow={props.handleSetEditingRow({ appSuiteName, tenant, startDate })}
                                    />
                                    <UserProfileUserGroups tenant={tenant} />
                                </section>
                                {  tenant.isLicenseRequired && <UserProfileLicensesList  tenant={tenant} /> }
                                <UserProfileAclsList tenant={tenant} />
                                <UserProfileRolesList  tenant={tenant} />
                                <UserProfileClaimsList tenant={tenant} />
                            </AccordionDetails>
                        </ErrorBoundary>
                    </Accordion>
                );
            })
            }
        </div>
    );
};
